/**
 * A video training, on this site.
 *
 * One component for all three; the content is in lib/trainingData. Same shape
 * as EcosystemPath, and for the same reason: three near-identical pages is how
 * the fifteen tool pages drifted apart in the first place.
 *
 * The page ends with the next step in the path the visitor came from. A
 * training that ends with the video ending is a dead end, and this one is
 * reached from a numbered path where there is an obvious next thing to do.
 */

import { useRef } from "react";
import { BUTTON } from "@/lib/buttonStyles";
import { Link, useRoute } from "wouter";
import { ArrowRight, Check, Clock } from "lucide-react";
import GuideShell from "@/components/GuideShell";
import NotFound from "@/pages/NotFound";
import { trainingBySlug } from "@/lib/trainingData";
import { useOpenVideoPlayer } from "@/hooks/useOpenVideoPlayer";

export default function TrainingPage() {
  const [, params] = useRoute("/trainings/:slug");
  const training = params?.slug ? trainingBySlug(params.slug) : undefined;

  // Hooks cannot be called conditionally, so the ref and the player hook run
  // whether or not the slug matched. The hook no-ops without a container.
  const videoRef = useRef<HTMLDivElement>(null);
  useOpenVideoPlayer(videoRef, training?.videoId ?? "", {
    enabled: Boolean(training),
  });

  if (!training) return <NotFound />;

  return (
    <GuideShell
      context={`${training.toolLabel} training`}
      backHref={`/resources/${training.toolSlug}`}
      backLabel={`Back to ${training.toolLabel}`}
    >
      <div className="container py-8 max-w-3xl">
        <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
          {training.toolLabel} training
        </p>
        <h1 className="font-['Fraunces'] text-3xl md:text-4xl font-bold text-foreground leading-tight mt-1">
          {training.title}
        </h1>
        <p className="text-base text-foreground/70 leading-relaxed mt-3">
          {training.description}
        </p>
        <p className="inline-flex items-center gap-1.5 text-sm text-muted-foreground mt-3">
          <Clock className="w-4 h-4" aria-hidden="true" />
          {training.minutes} minutes
        </p>

        {/* 16:9 via padding rather than aspect-ratio: the Open.Video script
            measures its container on load, and a container that is zero-height
            at that moment gets a zero-height player. */}
        <div
          className="relative w-full rounded-[10px] overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.13)] bg-muted mt-5"
          style={{ paddingBottom: "56.25%" }}
        >
          <div ref={videoRef} className="absolute inset-0 w-full h-full" />
        </div>

        <p className="text-base text-foreground/80 leading-relaxed mt-6">
          {training.intro}
        </p>

        <h2 className="font-['Fraunces'] text-xl font-bold text-foreground mt-8 mb-3">
          What you'll learn
        </h2>
        <ul className="space-y-2.5">
          {training.learn.map(line => (
            <li key={line} className="flex gap-2.5 text-base text-foreground/80">
              <Check
                className="w-4 h-4 shrink-0 mt-1 text-[oklch(0.38_0.09_155)]"
                aria-hidden="true"
              />
              <span className="leading-snug">{line}</span>
            </li>
          ))}
        </ul>

        <div className="rounded-xl border border-border bg-muted/40 p-4 sm:p-5 mt-8">
          <h2 className="font-['Fraunces'] text-lg font-bold text-foreground">
            This training is for you if
          </h2>
          <p className="text-base text-foreground/80 leading-relaxed mt-1.5">
            {training.forYou}
          </p>
        </div>

        {/* Where guides end. Same pattern as the quick-wins pages: the video
            finishing is not the end of anything, it is the middle of a path. */}
        <div className="rounded-xl border border-border bg-card p-4 sm:p-5 mt-6 shadow-sm">
          <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
            Next in your path
          </p>
          <Link
            href={training.next.href}
            className={`mt-2 ${BUTTON.primary}`}
          >
            {training.next.label}
            <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-sm text-muted-foreground mt-2">
            Or head{" "}
            <Link
              href={`/resources/${training.toolSlug}`}
              className="font-semibold text-foreground underline underline-offset-2 hover:opacity-80"
            >
              back to the full {training.toolLabel} path
            </Link>
            .
          </p>
        </div>
      </div>
    </GuideShell>
  );
}
